import { type FC, memo } from 'react'
import { useConversionRate } from 'v2/pages/billing/queries'
import { useFeatureFlags } from 'v2/shared/hooks/use-feature-flags'
import { formatCurrency } from '../utils'

interface CurrencyProps {
  value: number | string | undefined | null
  className?: string
  options?: Omit<Intl.NumberFormatOptions, 'style'>
  decimalPlaces?: number
  /**
   * Opts this display into the currency-conversion removal. When set and the
   * `isCurrencyConversionDisabled` flag is on, the value is rendered as-is —
   * the backend now sends the already-converted amount, so the frontend skips
   * applying its own conversion rate. Drop this prop (and the flag) once the
   * backend owns conversion everywhere to remove the frontend path entirely.
   */
  skipConversion?: boolean
}

const Currency: FC<CurrencyProps> = ({
  value,
  className = '',
  options = {},
  decimalPlaces,
  skipConversion = false,
}) => {
  const { conversionRate, currencyCode } = useConversionRate()
  const { isCurrencyConversionDisabled } = useFeatureFlags()

  if (value == null) return null

  const numericValue = typeof value === 'string' ? parseFloat(value) : value
  if (Number.isNaN(numericValue)) return null

  // When conversion is disabled the backend already returns the converted
  // amount, so display it as-is instead of applying the frontend rate.
  const effectiveRate = skipConversion && isCurrencyConversionDisabled ? 1 : conversionRate

  const minimumFractionDigits =
    decimalPlaces !== undefined ? decimalPlaces : currencyCode === 'INR' ? 2 : 4

  return (
    <span className={className}>
      {formatCurrency(numericValue * effectiveRate, currencyCode, true, {
        minimumFractionDigits,
        ...options,
      })}
    </span>
  )
}

export const CurrencyRenderer = memo(Currency)
